import { httpClient } from '../http-client';
import { buildQueryString } from './query-builder';
import { ApiResponse } from '../../types/responses/api-response';

// ===== SUPPLIER API TYPES =====

export interface SupplierMetrics {
  totalRevenue: number;
  activeOrders: number;
  completedOrders: number;
  averageRating: number;
  totalServices: number;
  monthlyRevenue: number;
  pendingOrders?: number;
  cancelledOrders?: number;
  revenueGrowth?: number;
}

export interface SupplierOrder {
  id: string;
  quoteId: string;
  eventId: string;
  eventTitle: string;
  serviceId: string;
  serviceName: string;
  clientId: string;
  clientName: string;
  totalPrice: number;
  status: 'pending' | 'accepted' | 'in-progress' | 'completed' | 'cancelled';
  deadline: string;
  priority: 'high' | 'medium' | 'low';
  createdAt: string;
  notes?: string;
}

export interface SupplierService {
  id: string;
  supplierId: string;
  name: string;
  description: string;
  category: string;
  price: {
    min: number;
    max: number;
    currency: string;
    perUnit?: string;
  };
  tags: string[];
  images: string[];
  rating: number;
  totalOrders: number;
  status: 'active' | 'inactive' | 'draft';
  createdAt: string;
  updatedAt: string;
}

export interface SupplierReview {
  id: string;
  supplierId: string;
  userId: string;
  userName: string;
  rating: number;
  comment: string;
  serviceId: string;
  serviceName: string;
  eventId?: string;
  createdAt: string;
}

export interface SupplierAnalytics {
  revenueByMonth: Array<{ month: string; revenue: number }>;
  ordersByStatus: Array<{ status: string; count: number }>;
  topServices: Array<{ 
    serviceId: string; 
    serviceName: string; 
    orders: number; 
    revenue: number 
  }>;
  clientRetention: number;
  averageOrderValue: number;
  conversionRate: number;
}

export interface SupplierProfile {
  id: string;
  userId: string;
  companyName: string;
  description: string;
  category: string;
  services: string[];
  rating: number;
  totalOrders: number;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  contactInfo: {
    email: string;
    phone: string;
    website?: string;
  };
  businessInfo: {
    inn?: string;
    ogrn?: string;
    legalAddress?: string;
  };
}

export interface SupplierDashboard {
  metrics: SupplierMetrics;
  recentOrders: SupplierOrder[];
  topServices: SupplierService[];
  recentReviews: SupplierReview[];
  alerts: Array<{
    type: 'warning' | 'info' | 'success';
    message: string;
  }>;
}

// Query параметры
export interface OrdersQuery {
  status?: 'pending' | 'accepted' | 'in-progress' | 'completed' | 'cancelled';
  priority?: 'high' | 'medium' | 'low';
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'deadline' | 'totalPrice';
  sortOrder?: 'asc' | 'desc';
}

export interface ServicesQuery {
  category?: string;
  status?: 'active' | 'inactive' | 'draft';
  page?: number;
  limit?: number;
}

export interface ReviewsQuery {
  page?: number;
  limit?: number;
  minRating?: number;
}

export interface CreateServiceData {
  name: string;
  description: string;
  category: string;
  price: {
    min: number;
    max: number;
    currency: string;
    perUnit?: string;
  };
  tags?: string[];
  images?: string[];
}

export interface UpdateServiceData extends Partial<CreateServiceData> {
  status?: 'active' | 'inactive' | 'draft';
}

export interface UpdateOrderStatusData {
  status: 'pending' | 'accepted' | 'in-progress' | 'completed' | 'cancelled';
  notes?: string;
}

export interface UpdateProfileData {
  companyName?: string;
  description?: string;
  category?: string;
  contactInfo?: {
    email?: string;
    phone?: string;
    website?: string;
  };
  businessInfo?: {
    inn?: string;
    ogrn?: string;
    legalAddress?: string;
  };
}

// ===== SUPPLIER API METHODS =====

export const supplierApi = {
  // ==================== ДАШБОРД ====================

  /**
   * Получить полный дашборд поставщика
   */
  getDashboard: async (): Promise<ApiResponse<SupplierDashboard>> => {
    return httpClient.get<SupplierDashboard>('/supplier/dashboard');
  },

  /**
   * Получить метрики поставщика
   */
  getMetrics: async (): Promise<ApiResponse<SupplierMetrics>> => {
    return httpClient.get<SupplierMetrics>('/supplier/metrics');
  },

  // ==================== ЗАКАЗЫ ====================

  /**
   * Получить список заказов
   */
  getOrders: async (query?: OrdersQuery): Promise<ApiResponse<{
    orders: SupplierOrder[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/supplier/orders${buildQueryString(query || {})}`);
  },

  /**
   * Получить детали заказа
   */
  getOrder: async (orderId: string): Promise<ApiResponse<SupplierOrder>> => {
    return httpClient.get<SupplierOrder>(`/supplier/orders/${orderId}`);
  },

  /**
   * Обновить статус заказа
   */
  updateOrderStatus: async (orderId: string, data: UpdateOrderStatusData): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.put(`/supplier/orders/${orderId}/status`, data);
  },

  // ==================== УСЛУГИ ====================

  /**
   * Получить список услуг поставщика
   */
  getServices: async (query?: ServicesQuery): Promise<ApiResponse<{
    services: SupplierService[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/supplier/services${buildQueryString(query || {})}`);
  },

  /**
   * Создать новую услугу
   */
  createService: async (data: CreateServiceData): Promise<ApiResponse<SupplierService>> => {
    return httpClient.post<SupplierService>('/supplier/services', data);
  },

  /**
   * Обновить услугу
   */
  updateService: async (serviceId: string, data: UpdateServiceData): Promise<ApiResponse<SupplierService>> => {
    return httpClient.put<SupplierService>(`/supplier/services/${serviceId}`, data);
  },

  /**
   * Удалить услугу
   */
  deleteService: async (serviceId: string): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.delete(`/supplier/services/${serviceId}`);
  },

  // ==================== ОТЗЫВЫ ====================

  /**
   * Получить отзывы о поставщике
   */
  getReviews: async (query?: ReviewsQuery): Promise<ApiResponse<{
    reviews: SupplierReview[];
    total: number;
    averageRating: number;
  }>> => {
    return httpClient.get(`/supplier/reviews${buildQueryString(query || {})}`);
  },

  // ==================== АНАЛИТИКА ====================

  /**
   * Получить аналитику поставщика
   */
  getAnalytics: async (period?: 'week' | 'month' | 'year'): Promise<ApiResponse<SupplierAnalytics>> => {
    return httpClient.get<SupplierAnalytics>(`/supplier/analytics${buildQueryString(period ? { period } : {})}`);
  },

  // ==================== ПРОФИЛЬ ====================

  /**
   * Получить профиль поставщика
   */
  getProfile: async (): Promise<ApiResponse<SupplierProfile>> => {
    return httpClient.get<SupplierProfile>('/supplier/profile');
  },

  /**
   * Обновить профиль поставщика
   */
  updateProfile: async (data: UpdateProfileData): Promise<ApiResponse<SupplierProfile>> => {
    return httpClient.put<SupplierProfile>('/supplier/profile', data);
  },

  // ==================== МОБИЛЬНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить мобильный дашборд
   */
  getMobileDashboard: async (): Promise<ApiResponse<{
    metrics: SupplierMetrics;
    urgentOrders: SupplierOrder[];
    quickStats: {
      todayRevenue: number;
      pendingOrders: number;
      newReviews: number;
    };
  }>> => {
    return httpClient.get('/supplier/mobile/dashboard');
  },

  /**
   * Получить срочные заказы
   */
  getUrgentOrders: async (limit: number = 10): Promise<ApiResponse<SupplierOrder[]>> => {
    return httpClient.get<SupplierOrder[]>(`/supplier/mobile/orders/urgent?limit=${limit}`);
  },
};

export default supplierApi;

